import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useWallet } from '../../contexts/wallet';
import { useUser } from '../../contexts/user';
import WalletContent from './wallet_content';
import WalletHistoryModal from './wallet_history_modal';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1900;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 200ms ease;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 460px;
  height: 460px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 16px;
  box-shadow: 0 20px 60px rgba(42, 26, 110, 0.18), 0 4px 16px rgba(0,0,0,0.08);
  @media (min-width: 768px) {
    max-width: 520px;
    height: 520px;
  }
  @media (min-width: 1200px) {
    max-width: 580px;
    height: 580px;
  }
`;

const WalletModal = () => {
  const {
    isOpen, closeWallet, balance, credits, currency, cashPayoutSummary, errorMessage, loading
  } = useWallet();
  const { user } = useUser();
  const [historyType, setHistoryType] = useState(null);

  const userName = user ? user.getFirstName() : '';

  const handleCheckPayouts = useCallback(() => setHistoryType('cash'), []);

  const handleCheckCredits = useCallback(() => setHistoryType('credit'), []);

  const handleClose = useCallback(() => {
    closeWallet();
    setHistoryType(null);
  }, [closeWallet]);

  const handleHistoryBack = useCallback(() => setHistoryType(null), []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') handleClose();
  }, [handleClose]);

  const handleStopPropagation = useCallback((e) => e.stopPropagation(), []);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  if (historyType) {
    return (
      <WalletHistoryModal type={historyType}
        userName={userName}
        currency={currency}
        credits={historyType === 'credit' ? credits : undefined}
        onClose={handleClose}
        onBack={handleHistoryBack} />
    );
  }

  return (
    <Overlay onClick={handleClose}>
      <Modal onClick={handleStopPropagation}>
        <WalletContent userName={userName}
          balance={balance}
          currency={currency}
          credits={credits}
          cashPayoutSummary={cashPayoutSummary}
          errorMessage={errorMessage}
          loading={loading}
          onClose={handleClose}
          onCheckAllPayouts={handleCheckPayouts}
          onCheckAllCredits={handleCheckCredits} />
      </Modal>
    </Overlay>
  );
};

export default WalletModal;
